import { Tag } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { semantic, type RoleAccent } from '@/theme/tokens';
import type { RoleId } from '@/auth/roles';

export interface RoleBadgeProps {
  role: RoleId;
  /** Small = table rows / activity logs. Default is medium. */
  size?: 'small' | 'medium';
  /** Show the persona icon. */
  showIcon?: boolean;
}

/**
 * Displays a persona chip with the role's accent color.
 *
 * Used in: role switcher label, activity/audit logs, attribution lines
 * ("created by <RoleBadge />"), dashboard hero strip.
 *
 * Accent colors live in `tokens.semantic.role.*`; never inline colors here.
 */
export default function RoleBadge({ role, size = 'medium', showIcon }: RoleBadgeProps) {
  const { t } = useTranslation('common');
  const color = semantic.role[role as RoleAccent] ?? semantic.role.bilingualViewer;
  const dense = size === 'small';

  return (
    <Tag
      color={color}
      icon={showIcon ? <UserOutlined /> : null}
      style={{
        marginInlineEnd: 0,
        fontSize: dense ? 11 : 12,
        padding: dense ? '0 6px' : '0 8px',
        lineHeight: dense ? '18px' : '22px',
        borderRadius: 4,
      }}
    >
      {t(`roles.${role}`)}
    </Tag>
  );
}
